import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, Save, Loader2, Tag as TagIcon, FileText } from "lucide-react";
import api from "@/lib/api";

const inp = "w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary";
const btnSm = "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors";


function SnippetRow({ snippet, onSaved, onDeleted, categoryLabel }) {
  const [label, setLabel] = useState(snippet.label || "");
  const [text, setText] = useState(snippet.text || "");
  const [active, setActive] = useState(snippet.active !== false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const save = async () => {
    if (!label.trim() || !text.trim()) { toast.error("Label e texto são obrigatórios"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/rt-note-snippets/${snippet.id}`, {
        label: label.trim(), text: text.trim(), active,
      });
      toast.success("Snippet guardado");
      setDirty(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a guardar");
    } finally { setBusy(false); }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/rt-note-snippets/${snippet.id}`, { active: !active });
      setActive(!active);
      onSaved?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(`Eliminar snippet "${snippet.label}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/rt-note-snippets/${snippet.id}`);
      toast.success("Eliminado");
      onDeleted?.(snippet.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); setBusy(false); }
  };

  return (
    <tr data-testid={`rt-snip-row-${snippet.id}`} className={`border-t border-border ${active ? "" : "bg-muted/40"}`}>
      <td className="p-2 w-40">
        <input
          data-testid={`rt-snip-label-${snippet.id}`}
          className={inp}
          value={label}
          onChange={(e) => { setLabel(e.target.value); setDirty(true); }}
          disabled={busy}
        />
      </td>
      <td className="p-2">
        <textarea
          data-testid={`rt-snip-text-${snippet.id}`}
          className={`${inp} font-mono text-xs`}
          rows={2}
          value={text}
          onChange={(e) => { setText(e.target.value); setDirty(true); }}
          disabled={busy}
        />
      </td>
      <td className="p-2 w-20">
        {snippet.is_seed ? (
          <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">Seed</span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--success))] font-semibold">Custom</span>
        )}
      </td>
      <td className="p-2 w-56 whitespace-nowrap">
        <div className="flex items-center gap-1">
          {dirty && (
            <button
              data-testid={`rt-snip-save-${snippet.id}`}
              onClick={save} disabled={busy}
              className={`${btnSm} bg-foreground hover:bg-foreground/90 text-background disabled:opacity-50`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar
            </button>
          )}
          <button
            data-testid={`rt-snip-toggle-${snippet.id}`}
            onClick={toggle} disabled={busy}
            className={`${btnSm} ${active ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] hover:bg-emerald-200" : "bg-muted text-muted-foreground hover:bg-slate-300"} disabled:opacity-50`}
          >
            {active ? <><Eye className="w-3 h-3" /> Activa</> : <><EyeOff className="w-3 h-3" /> Inactiva</>}
          </button>
          <button
            data-testid={`rt-snip-delete-${snippet.id}`}
            onClick={doDelete} disabled={busy}
            className={`${btnSm} bg-[hsl(var(--danger-bg))] hover:bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border border-[hsl(var(--danger))]/25 disabled:opacity-50`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}


function CategorySection({ categoryKey, categoryLabel, snippets, onChanged }) {
  const [newLabel, setNewLabel] = useState("");
  const [newText, setNewText] = useState("");
  const [adding, setAdding] = useState(false);

  const doAdd = async () => {
    const l = newLabel.trim();
    const t = newText.trim();
    if (!l || !t) { toast.error("Label e texto são obrigatórios"); return; }
    setAdding(true);
    try {
      await api.post("/rt-note-snippets", { category: categoryKey, label: l, text: t });
      toast.success(`Snippet "${l}" criado`);
      setNewLabel(""); setNewText("");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setAdding(false); }
  };

  return (
    <section data-testid={`rt-snip-section-${categoryKey}`} className="bg-card border border-border rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
          <TagIcon className="w-4 h-4 text-primary" />
        </div>
        <h3 className="font-ui text-base text-foreground">{categoryLabel}</h3>
        <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold px-2 py-0.5 rounded-full bg-muted">
          {snippets.length}
        </span>
      </div>
      <div className="grid md:grid-cols-[minmax(0,160px)_minmax(0,1fr)_auto] gap-2 mb-3">
        <input
          data-testid={`rt-snip-new-label-${categoryKey}`}
          className={inp} placeholder="Rótulo curto (chip)"
          value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
          disabled={adding}
        />
        <input
          data-testid={`rt-snip-new-text-${categoryKey}`}
          className={inp} placeholder="Texto inserido no bloco de notas ao clicar…"
          value={newText} onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doAdd(); } }}
          disabled={adding}
        />
        <button
          data-testid={`rt-snip-add-${categoryKey}`}
          onClick={doAdd} disabled={adding || !newLabel.trim() || !newText.trim()}
          className="inline-flex items-center gap-1 bg-foreground hover:bg-foreground/90 text-background rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-40"
        >
          {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Adicionar
        </button>
      </div>
      {snippets.length === 0 ? (
        <div className="text-sm text-muted-foreground italic">Sem snippets nesta categoria.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">
                <th className="p-2 w-40">Label (chip)</th>
                <th className="p-2">Texto inserido</th>
                <th className="p-2 w-20">Origem</th>
                <th className="p-2 w-56">Acções</th>
              </tr>
            </thead>
            <tbody>
              {snippets.map((s) => (
                <SnippetRow
                  key={s.id}
                  snippet={s}
                  categoryLabel={categoryLabel}
                  onSaved={onChanged}
                  onDeleted={onChanged}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


export default function RTNotesSnippetsSettings() {
  const [loading, setLoading] = useState(true);
  const [snippets, setSnippets] = useState([]);
  const [catLabels, setCatLabels] = useState({});

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/rt-note-snippets");
      setSnippets(r.data?.snippets || []);
      setCatLabels(r.data?.category_labels || {});
    } catch (e) { toast.error("Erro a carregar snippets"); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const grouped = {};
  for (const s of snippets) (grouped[s.category] ||= []).push(s);

  return (
    <div data-testid="rt-notes-snippets-settings" className="p-8 lg:p-12 max-w-[1200px] mx-auto space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Configuração</div>
        <h1 className="font-display text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
          <FileText className="w-8 h-8 text-primary" />
          Textos rápidos da RT
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
          Gere as tags/snippets que o Consultor de Cuidados pode inserir com um clique no
          bloco de notas durante a Reunião Técnica. Cada tag pertence a uma categoria clínica
          que também define para que campo do plano de cuidados a IA vai mapear no post-processing.
        </p>
      </header>

      {loading ? (
        <div className="text-sm text-muted-foreground italic text-center py-12">A carregar…</div>
      ) : (
        <div className="space-y-4">
          {Object.keys(catLabels).map((cat) => (
            <CategorySection
              key={cat}
              categoryKey={cat}
              categoryLabel={catLabels[cat]}
              snippets={grouped[cat] || []}
              onChanged={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
}
